import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react'
import { useI18n } from '@/lib/i18n'
import { announcementStore, contactStore, serviceStore } from '@/lib/variables'
import type { Section } from '@/lib/sections'
import {
  ANNOUNCEMENT_ID,
  ANNOUNCEMENT_KEY,
  isDismissed,
  nextMilestone,
  remaining,
  type MilestoneKey,
  type Remaining,
} from '@/lib/announcement'
import MazeArt from '@/components/ui/MazeArt'
import LanguageSwitcher from '@/components/ui/LanguageSwitcher'
import './Announcement.css'

/**
 * Full-screen announcement of the NISG 2026 cybersecurity service: a
 * gradient wipe opens a paper panel, the copy steps in, a live countdown
 * runs to the next statutory deadline and the brand maze draws its route
 * behind it. On phones it is a compact banner at the bottom instead, so it
 * never counts as an intrusive interstitial for search engines. "See the
 * service" opens the cybersecurity entry in the services list (with the
 * self-check); the hero tape reopens the announcement on demand. Shown once per
 * visitor and 30 days (localStorage, listed in PrivacyPage), never in the
 * prerender: `open` only flips in an effect.
 */

type Fact = { label: string; value: string }
type Copy = {
  label: string
  eyebrow: string
  title: string
  lead: string
  offer: string
  facts: Fact[]
  countdown: Record<MilestoneKey | 'over' | 'days' | 'hours' | 'minutes' | 'seconds', string>
  ticker: string
  cta: string
  ctaSecondary: string
  close: string
  disclaimer: string
}

const SERVICE_ID = 'cybersecurity'
const OPEN_DELAY_MS = 900
const CLOSE_MS = 380

const pad = (n: number) => String(n).padStart(2, '0')

export default function Announcement({ onNavigate }: { onNavigate: (s: Section) => void }) {
  const { section, t } = useI18n()
  const c = section('announcement') as unknown as Copy
  const facts = Array.isArray(c.facts) ? c.facts : []

  const [open, setOpen] = useState(false)
  const [closing, setClosing] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    try {
      if (isDismissed(localStorage.getItem(ANNOUNCEMENT_KEY), Date.now())) return
    } catch {
      /* storage unavailable: show it */
    }
    const id = setTimeout(() => setOpen(true), OPEN_DELAY_MS)
    return () => clearTimeout(id)
  }, [])

  // The hero tape reopens it at any time, dismissed or not.
  useEffect(
    () =>
      announcementStore.on(() => {
        setClosing(false)
        setOpen(true)
      }),
    [],
  )

  // Lock the page behind the overlay and start on the close button.
  useEffect(() => {
    if (!open) return
    const prev = document.documentElement.style.overflow
    document.documentElement.style.overflow = 'hidden'
    panelRef.current?.focus({ preventScroll: true })
    return () => {
      document.documentElement.style.overflow = prev
    }
  }, [open])

  const dismiss = (after?: () => void) => {
    if (closing) return
    try {
      localStorage.setItem(ANNOUNCEMENT_KEY, JSON.stringify({ id: ANNOUNCEMENT_ID, dismissedAt: Date.now() }))
    } catch {
      /* ignore */
    }
    setClosing(true)
    setTimeout(() => {
      setOpen(false)
      setClosing(false)
      after?.()
    }, CLOSE_MS)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      dismiss()
      return
    }
    if (e.key !== 'Tab' || !panelRef.current) return
    const items = Array.from(panelRef.current.querySelectorAll<HTMLElement>('button, a[href]')).filter((el) => !el.hidden)
    if (items.length === 0) return
    const first = items[0]
    const last = items[items.length - 1]
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }

  if (!open) return null

  const words = c.title.split(' ')

  return (
    <div
      className={`ann ${closing ? 'is-closing' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={c.label}
      onKeyDown={onKeyDown}
    >
      <div className="ann__backdrop" onClick={() => dismiss()} aria-hidden />
      <div className="ann__panel" ref={panelRef} tabIndex={-1}>
        <span className="ann__wipe" aria-hidden />
        <span className="ann__ribbon" aria-hidden />

        <div className="ann__lang">
          <LanguageSwitcher />
        </div>
        <button type="button" className="ann__close" onClick={() => dismiss()} aria-label={c.close}>
          <span aria-hidden>×</span>
        </button>

        <div className="ann__body">
        <div className="ann__copy">
          <span className="eyebrow ann__eyebrow" data-reveal style={{ '--i': 0 } as CSSProperties}>
            <span className="ann__pulse" aria-hidden />
            {c.eyebrow}
          </span>

          <h2 className="ann__title" aria-label={c.title}>
            {words.map((w, i) => (
              <span className="ann__word" key={`${w}-${i}`} aria-hidden>
                <span style={{ '--i': i } as CSSProperties}>{w}</span>
              </span>
            ))}
          </h2>

          <p className="ann__lead" data-reveal style={{ '--i': words.length + 1 } as CSSProperties}>
            {c.lead}
          </p>

          <dl className="ann__facts" data-reveal style={{ '--i': words.length + 2 } as CSSProperties}>
            {facts.map((f, i) => (
              <div key={f.label} className="ann__fact" style={{ '--i': words.length + 3 + i } as CSSProperties}>
                <dt className="mono">{f.label}</dt>
                <dd>{f.value}</dd>
              </div>
            ))}
          </dl>

          <p className="ann__offer" data-reveal style={{ '--i': words.length + 6 } as CSSProperties}>
            {c.offer}
          </p>

          <div className="ann__actions" data-reveal style={{ '--i': words.length + 7 } as CSSProperties}>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() =>
                dismiss(() => {
                  contactStore.prefill({ subject: t('inquiry.nisgSubject'), message: t('inquiry.nisgMessage') })
                  onNavigate('contact')
                })
              }
            >
              {c.cta}
              <span className="arrow" aria-hidden>
                →
              </span>
            </button>
            <button
              type="button"
              className="btn"
              onClick={() =>
                dismiss(() => {
                  serviceStore.open(SERVICE_ID)
                  onNavigate('about')
                })
              }
            >
              {c.ctaSecondary}
            </button>
          </div>
          <p className="ann__disclaimer mono" data-reveal style={{ '--i': words.length + 8 } as CSSProperties}>
            {c.disclaimer}
          </p>
        </div>

        <div className="ann__side" data-reveal style={{ '--i': 3 } as CSSProperties}>
          <figure className="ann__maze" aria-hidden>
            <MazeArt cols={14} rows={18} seed={94} />
          </figure>
          <Countdown copy={c.countdown} />
        </div>
        </div>

        <div className="ann__ticker" aria-hidden>
          <span>
            {c.ticker} · {c.ticker} ·{' '}
          </span>
        </div>
      </div>
    </div>
  )
}

function Countdown({ copy }: { copy: Copy['countdown'] }) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])
  const next = nextMilestone(now)
  if (!next) {
    return <p className="ann__over">{copy.over}</p>
  }
  const r = remaining(next.at, now)
  const cells: { key: keyof Remaining; value: string }[] = [
    { key: 'days', value: String(r.days) },
    { key: 'hours', value: pad(r.hours) },
    { key: 'minutes', value: pad(r.minutes) },
    { key: 'seconds', value: pad(r.seconds) },
  ]
  return (
    <div className="ann__countdown" role="timer" aria-live="off">
      <p className="ann__countdown-label">{copy[next.key]}</p>
      <div className="ann__digits">
        {cells.map((cell) => (
          <div key={cell.key} className="ann__cell">
            <span className="ann__num">
              {/* a new key per value re-mounts the digits, which replays the tick */}
              <span key={cell.value} className="ann__num-in">
                {cell.value}
              </span>
            </span>
            <span className="ann__unit mono">{copy[cell.key]}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
